import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ClickAwayListener, Popper } from '@mui/material';
import { UnfoldMoreRounded } from '@mui/icons-material';
import classNames from 'classnames';
import studioIcon from '@src/assets/icons/tts-new.svg';
import educatorsIcon from '@src/assets/icons/educators.webp';
import ROUTES from '@src/constants/route';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { PRODUCT_MODE } from '@src/constants/educators';
import useProductMode from '@src/hooks/useProductMode';
import { useCheckProjectRoute } from '@src/hooks/useCheckDubbingRoute';
import { StyledSidebarTooltip } from '../DesktopSidebar/index.style';
import { StyledProductSwitcher, StyledSwitcherPanel } from './index.style';

const MODES = [
  {
    id: PRODUCT_MODE.STUDIO,
    path: ROUTES.TTS,
    icon: studioIcon,
    labelKey: 'vbeeStudio',
    descriptionKey: 'vbeeStudioDescription',
  },
  {
    id: PRODUCT_MODE.EDUCATORS,
    path: ROUTES.EDUCATORS_HOME,
    icon: educatorsIcon,
    labelKey: 'vbeeForEducators',
    descriptionKey: 'vbeeForEducatorsDescription',
  },
];

const ProductSwitcher = ({ expanded, onNavigate }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const changeProductMode = useProductMode();
  const { getFeatureValue } = useFeatureFlags();
  const userId = useSelector((state) => state.auth.user?.id);
  const email = useSelector((state) => state.auth.user?.email);
  const phoneNumber = useSelector((state) => state.auth.user?.phoneNumber);
  const productMode = useSelector((state) => state.layout.productMode);
  const isDetailProjectPage = useCheckProjectRoute();
  const [anchorEl, setAnchorEl] = useState(null);

  const educatorsModeEnabled = getFeatureValue(FEATURE_KEYS.EDUCATORS_MODE, {
    userId,
    email,
    phoneNumber,
  });

  const activeMode = MODES.find((mode) => mode.id === productMode) || MODES[0];
  const open = Boolean(anchorEl);

  const handleToggle = (e) =>
    setAnchorEl((prev) => (prev ? null : e.currentTarget));

  const handleSelect = (mode) => {
    setAnchorEl(null);
    changeProductMode(mode.id);
    if (mode.path !== location.pathname) history.push(mode.path);
    if (onNavigate) onNavigate();
  };

  // Off means the sidebar looks exactly as it did before this feature.
  if (!educatorsModeEnabled) return null;
  // The project detail sidebar is an editing toolbar, not navigation.
  if (isDetailProjectPage) return null;

  const trigger = (
    <div
      className={classNames('switcher-trigger', {
        'switcher-trigger-collapsed': !expanded,
      })}
      onClick={handleToggle}
      role="button"
      tabIndex={0}
    >
      <img
        src={activeMode.icon}
        alt={t(activeMode.labelKey)}
        className="trigger-icon"
      />
      {expanded && (
        <>
          <span className="switcher-label">{t(activeMode.labelKey)}</span>
          <UnfoldMoreRounded className="switcher-caret" />
        </>
      )}
    </div>
  );

  return (
    <StyledProductSwitcher>
      {expanded ? (
        trigger
      ) : (
        <StyledSidebarTooltip title={t(activeMode.labelKey)}>
          {trigger}
        </StyledSidebarTooltip>
      )}
      {open && (
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <Popper
            open={open}
            anchorEl={anchorEl}
            placement={expanded ? 'bottom-start' : 'right-start'}
            style={{ zIndex: 1299 }}
            modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
          >
            <StyledSwitcherPanel
              // Never narrower than the trigger it hangs from; the content
              // decides how much wider it gets.
              style={{ minWidth: expanded ? anchorEl.offsetWidth : undefined }}
            >
              {MODES.map((mode) => (
                <div
                  key={mode.id}
                  className={classNames('switcher-item', {
                    'switcher-item-active': mode.id === activeMode.id,
                  })}
                  onClick={() => handleSelect(mode)}
                  role="button"
                  tabIndex={0}
                >
                  <img
                    src={mode.icon}
                    alt={t(mode.labelKey)}
                    className="item-icon"
                  />
                  <div className="switcher-item-text">
                    <span className="switcher-item-title">
                      {t(mode.labelKey)}
                    </span>
                    <span className="switcher-item-description">
                      {t(mode.descriptionKey)}
                    </span>
                  </div>
                </div>
              ))}
            </StyledSwitcherPanel>
          </Popper>
        </ClickAwayListener>
      )}
    </StyledProductSwitcher>
  );
};

export default ProductSwitcher;
